import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import {
  OwnerPatchBrandingRequestSchema,
  type OwnerPatchBrandingRequest,
} from '@naya/shared'

import { OwnerApiError } from '../ownerApi.js'
import { usePatchBranding } from '../hooks/useBranding.js'

/**
 * Business-name editor — a single text field + save button. Used by the
 * branding page and the setup wizard's step 2. `onSaved` lets the wizard
 * advance after a successful save; the branding page just shows a toast.
 */
export function BusinessNameForm({
  initialName,
  saveLabel = 'Save',
  onSaved,
}: {
  initialName: string
  saveLabel?: string
  onSaved?: (name: string) => void
}) {
  const patch = usePatchBranding()
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<OwnerPatchBrandingRequest>({
    resolver: zodResolver(OwnerPatchBrandingRequestSchema),
    defaultValues: { business_name: initialName },
  })

  function onSubmit(values: OwnerPatchBrandingRequest) {
    patch.mutate(values, {
      onSuccess: (res) => {
        toast.success('Business name saved')
        onSaved?.(res.tenant.business_name)
      },
      onError: (err) => {
        const message =
          err instanceof OwnerApiError
            ? err.userMessage
            : 'Could not save. Please try again.'
        setError('business_name', { message })
      },
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="owner-field">
        <label className="owner-label" htmlFor="business_name">
          Business name
        </label>
        <input
          id="business_name"
          type="text"
          className="owner-input"
          autoComplete="organization"
          placeholder="Gotchaa"
          aria-invalid={errors.business_name ? 'true' : undefined}
          {...register('business_name')}
        />
        {errors.business_name && (
          <p className="owner-field-error">{errors.business_name.message}</p>
        )}
      </div>
      <button
        type="submit"
        className="owner-btn owner-btn-primary"
        disabled={isSubmitting || patch.isPending || (!isDirty && !onSaved)}
      >
        {patch.isPending ? 'Saving…' : saveLabel}
      </button>
    </form>
  )
}
